// const API_URL = "http://localhost:3001/api";
// const API_URL = "https://api.piggymate.org/api";
import { config } from "./Config";

const API_URL = `${config.baseUrl}/api`;

// Helper function to get token from localStorage
export const getToken = () => localStorage.getItem("token");

const logError = (message, error) => {
  if (process.env.NODE_ENV !== "production") {
    console.error(message, {
      timestamp: new Date().toISOString(),
      error: error.message,
      code: error.code,
      stack: error.stack,
    });
  }
};

const fetchWithAuth = async (endpoint, options = {}, retries = 3) => {
  const token = getToken();

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.details || "Request failed");
      }
      return data;
    } catch (error) {
      logError(`API Request Failed (Attempt ${attempt}/${retries}):`, error);

      if (
        (error.code === "ECONNRESET" ||
          error.message.includes("fetch failed")) &&
        attempt < retries
      ) {
        // Wait for a bit before retrying (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, attempt) * 1000)
        );
        continue;
      }
      // If we're out of retries or it's not a connection error, throw
      throw error;
    }
  }
};

//Start of API Calls

// Login user
export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_URL}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.details || "Login failed");
    }

    // Store the token in localStorage
    localStorage.setItem("token", data.token);
    return data;
  } catch (error) {
    logError("Login error: ", error);
    throw error;
  }
};

export const createAccount = async (
  firstName,
  lastName,
  email,
  password,
  birthdate,
  pin,
  familyName
) => {
  try {
    const response = await fetch(`${API_URL}/create-account`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        password,
        birthdate,
        pin,
        familyName,
      }),
    });

    const data = await response.json();
    // Store the token in localStorage
    // localStorage.setItem("token", data.token);

    if (!response.ok) {
      throw new Error(data.details || "Account Creation failed");
    }

    return data;
  } catch (error) {
    logError("Account Creation error: ", error);
    throw error;
  }
};

export const fetchChildren = async (familyID) => {
  try {
    const data = await fetchWithAuth("/children", {
      method: "POST",
      body: JSON.stringify({ familyID }),
    });

    return data.Children;
  } catch (error) {
    logError("Error fetching users:", error);
    throw error;
  }
};

export const addChildren = async (
  childrenList,
  guardianID,
  familyName,
  email,
  primaryRate,
  doCreateToken,
  secondaryRate = null,
  cappedPrimaryInterestValue = null
) => {
  try {
    const response = await fetch(`${API_URL}/add-children`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        childrenList,
        guardianID,
        familyName,
        email,
        primaryRate,
        secondaryRate,
        cappedPrimaryInterestValue,
        doCreateToken,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.details || "Child addition failed");
    }
    return data;
  } catch (error) {
    logError("Child Addition error: ", error);
    throw error;
  }
};

export const fetchJobs = async (familyID) => {
  try {
    const data = await fetchWithAuth("/jobs", {
      method: "POST",
      body: JSON.stringify({ familyID }),
    });

    return data;
  } catch (error) {
    logError("Error fetching users:", error);
    throw error;
  }
};

export const fetchJobApprovals = async (familyID) => {
  try {
    const data = await fetchWithAuth("/job-approvals", {
      method: "POST",
      body: JSON.stringify({ familyID }),
    });

    return data;
  } catch (error) {
    logError("Error fetching users:", error);
    throw error;
  }
};

export const addFunds = async (amount, description, checkedOptions) => {
  try {
    amount = Math.abs(amount);
    const data = await fetchWithAuth("/update-funds", {
      method: "POST",
      body: JSON.stringify({ amount, description, checkedOptions }),
    });

    return data;
  } catch (error) {
    logError("Add Funds Error: ", error);
    throw error;
  }
};

//Add Savings Goal
export const addSavingsGoal = async (goalName, goalAmount, childID) => {
  try {
    const data = await fetchWithAuth("/add-savings-goal", {
      method: "POST",
      body: JSON.stringify({ goalName, goalAmount, childID }),
    });

    return data;
  } catch (error) {
    logError("New Savings Goal Addition error: ", error);
    throw error;
  }
};

//Add Job
export const addJob = async (jobName, jobPaymentAmount, familyID) => {
  try {
    const data = await fetchWithAuth("/add-job", {
      method: "POST",
      body: JSON.stringify({ jobName, jobPaymentAmount, familyID }),
    });

    return data;
  } catch (error) {
    logError("New Job Addition error: ", error);
    throw error;
  }
};

// gather child monlthy balance History
export const fetchMonthlyBalanceHistory = async (childID) => {
  try {
    const data = await fetchWithAuth(`/balance-history/${childID}`);

    return data;
  } catch (error) {
    logError("Error fetching balance history:", error);
  }
};

export const subtractFunds = async (amount, description, checkedOptions) => {
  try {
    amount = Math.abs(amount) * -1;
    const data = await fetchWithAuth("/update-funds", {
      method: "POST",
      body: JSON.stringify({ amount, description, checkedOptions }),
    });

    return data;
  } catch (error) {
    logError("Subtract Funds Error: ", error);
    throw error;
  }
};

export const fetchTransactions = async (familyID) => {
  try {
    const data = await fetchWithAuth("/family-transactions", {
      method: "POST",
      body: JSON.stringify({ familyID }),
    });

    return data;
  } catch (error) {
    logError("Error fetching family transactions:", error);
  }
};

export const fetchChildTransactions = async (childID) => {
  try {
    const data = await fetchWithAuth("/child-transactions", {
      method: "POST",
      body: JSON.stringify({ childID }),
    });

    return data;
  } catch (error) {
    logError("Error fetching family transactions:", error);
  }
};

// gather interest rates for a child
export const fetchInterestRates = async (childID) => {
  try {
    const data = await fetchWithAuth(`/interest/${childID}`);
    return data;
  } catch (error) {
    logError("Error fetching interest rates:", error);
  }
};

export const fetchChildSavingsInfo = async (childID) => {
  try {
    const data = await fetchWithAuth(`/child-savings-goals/${childID}`);
    return data;
  } catch (error) {
    logError("Error fetching savings goals:", error);
  }
};

// gather balance for a child
export const fetchChildBalance = async (childID) => {
  try {
    const data = await fetchWithAuth(`/child-balance/${childID}`);
    return data;
  } catch (error) {
    logError("Error fetching child balance:", error);
  }
};

export const deleteChildGoal = async (id) => {
  try {
    const data = await fetchWithAuth("/remove-goal", {
      method: "DELETE",
      body: JSON.stringify({ id }),
    });

    return;
  } catch (error) {
    logError("Error fetching interest rates:", error);
  }
};

export const fetchAccountInfo = async (username) => {
  try {
    const data = await fetchWithAuth(`/get-account-info/${username}`);
    return data;
  } catch (error) {
    logError("Error fetching account info:", error);
  }
};

export const fetchFamilyInterestRate = async (guardianID) => {
  try {
    const data = await fetchWithAuth(`/get-family-interest-rate/${guardianID}`);
    return data;
  } catch (error) {
    logError("Error fetching family interest rate:", error);
  }
};

export const updateAccountInfo = async (
  firstName,
  lastName,
  email,
  birthdate,
  pin,
  familyName,
  guardianID,
  familyID
) => {
  try {
    const data = await fetchWithAuth("/update-account-info", {
      method: "POST",
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        birthdate,
        pin,
        familyName,
        guardianID,
        familyID,
      }),
    });

    // Store the token in localStorage
    localStorage.setItem("token", data.token);

    return data;
  } catch (error) {
    logError("Account Info update failed: ", error);
    throw error;
  }
};

export const updateFamilyInterestRates = async (
  primaryInterestRate,
  secondaryInterestRate,
  maxPrimaryInterestValue,
  guardianID
) => {
  try {
    const data = await fetchWithAuth("/update-interest", {
      method: "POST",
      body: JSON.stringify({
        primaryInterestRate,
        secondaryInterestRate,
        maxPrimaryInterestValue,
        guardianID,
      }),
    });

    return data;
  } catch (error) {
    logError("Error updating interest rates:", error);
  }
};

export const completeJobForChild = async (childID, selectedJob, familyID) => {
  try {
    const data = await fetchWithAuth("/complete-job", {
      method: "POST",
      body: JSON.stringify({ childID, selectedJob, familyID }),
    });

    return data;
  } catch (error) {
    throw error;
  }
};

export const approveJobForChild = async (selectedJob, familyID) => {
  try {
    const data = await fetchWithAuth("/approve-job", {
      method: "POST",
      body: JSON.stringify({ selectedJob, familyID }),
    });

    return data;
  } catch (error) {
    throw error;
  }
};

export const rejectJobForChild = async (selectedJob, familyID) => {
  try {
    const data = await fetchWithAuth("/reject-job", {
      method: "POST",
      body: JSON.stringify({ selectedJob, familyID }),
    });

    return data;
  } catch (error) {
    throw error;
  }
};

export const confirmParentPin = async (familyID, pin) => {
  try {
    const data = await fetchWithAuth("/confirm-family-pin", {
      method: "POST",
      body: JSON.stringify({ familyID, pin }),
    });

    return data;
  } catch (error) {
    throw error;
  }
};

export const updatePassword = async (guardianID, oldPassword, newPassword) => {
  try {
    const data = await fetchWithAuth("/change-password", {
      method: "POST",
      body: JSON.stringify({
        guardianID,
        oldPassword,
        newPassword,
      }),
    });

    return data;
  } catch (error) {
    logError("Error changing password:", error);
    throw error;
  }
};

export const updateSavingsGoal = async (goalID, goalName, goalAmount) => {
  try {
    const data = await fetchWithAuth("/update-savings-goal", {
      method: "POST",
      body: JSON.stringify({
        goalID,
        goalName,
        goalAmount,
      }),
    });

    return data;
  } catch (error) {
    logError("Error updating Goal:", error);
    throw error;
  }
};

export const fetchChildInfo = async (childID) => {
  try {
    const data = await fetchWithAuth(`/get-child-info/${childID}`);

    return data;
  } catch (error) {
    logError("Error fetching child info:", error);
  }
};

export const updateChildInfo = async (firstName, childID) => {
  try {
    const data = await fetchWithAuth("/update-child-info", {
      method: "POST",
      body: JSON.stringify({
        firstName,
        childID,
      }),
    });

    return data;
  } catch (error) {
    logError("Error fetching child info:", error);
  }
};

export const sendPasswordResetEmail = async (email) => {
  try {
    const response = await fetch(`${API_URL}/reset-password-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending reset password email:", error);
  }
};

export const resetPassword = async (token, newPassword) => {
    const response = await fetch(`${API_URL}/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        newPassword,
        token,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
};
